/**
 * Panneau « plateau TV » au survol (ou tap) d'une carte de match de la bande
 * « Aujourd'hui ». Affiche, avec des barres, tout ce qu'on peut produire avec
 * le PLAN GRATUIT (football-data + openfootball) ET notre moteur :
 *  - probabilité 1N2 du modèle (barre segmentée) ;
 *  - rapport de force Elo (barre) ;
 *  - buts attendus / xG-Poisson (barre) ;
 *  - forme récente (pastilles V/N/D) ;
 *  - classement de groupe ;
 *  - score réel + buteurs (quand le match est joué).
 *
 * NB : possession / tirs / corners / xG réels ne sont PAS dans le tier gratuit
 * de football-data (add-on payant). On maximise donc le gratuit + le modèle.
 */
import type { OfficialState } from "../app/officialStore.ts";
import { lambdas, outcomeProbabilities, winProbability } from "../engine/poisson.ts";
import { initialElos, hosts, poissonParams, modelHomeAdvantage } from "../app/data.ts";
import { esc, flagSpan, flagCode, teamName, roundName, frontZ } from "./html.ts";
import { teamRatingTag } from "./teamRatingTag.ts";
import { fmtPct, fmtTime } from "../app/format.ts";
import { duoStat } from "./components/statBars.ts";
import { statMeta, STAT_ORDER } from "./components/statGraph.ts";
import { animateStatBars, eventsHtml, lineupsSection } from "./components/liveFocus.ts";
import { computeDominance, deservedResult } from "../app/metrics.ts";
import { fetchH2H, h2hRecord, type H2HMeeting } from "../app/h2hData.ts";
import { track } from "../app/track.ts";
import { t } from "../app/i18n.ts";

type Form = ReadonlyArray<"w" | "l" | "d">;
interface Standing {
  rank: number;
  points: number;
  goalDiff: number;
}

function teamForm(state: OfficialState, team: string): Form {
  const out: Array<{ id: number; r: "w" | "l" | "d" }> = [];
  for (const v of state.matches) {
    if (v.match.stage !== "group" || (v.match.home !== team && v.match.away !== team)) continue;
    if (v.status !== "finished" || !v.displayScore) continue;
    const isHome = v.match.home === team;
    const gf = v.displayScore[isHome ? 0 : 1];
    const ga = v.displayScore[isHome ? 1 : 0];
    out.push({ id: v.match.id, r: gf > ga ? "w" : gf < ga ? "l" : "d" });
  }
  for (const [nid, node] of Object.entries(state.bracket)) {
    if (node.a !== team && node.b !== team) continue;
    const view = state.byId.get(Number(nid.slice(1)));
    if (!view?.displayScore) continue;
    out.push({ id: Number(nid.slice(1)), r: node.winner === team ? "w" : "l" });
  }
  out.sort((a, b) => a.id - b.id);
  return out.slice(-5).map((x) => x.r);
}

function standingOf(state: OfficialState, team: string): Standing | null {
  for (const s of state.standings) {
    const row = s.rows.find((r) => r.team === team);
    if (row) return { rank: row.rank, points: row.points, goalDiff: row.goalDiff };
  }
  return null;
}

const pct = (x: number): string => `${(x * 100).toFixed(1)}%`;
const ord = (r: number): string => `${r}<sup>${r === 1 ? t("common.ordinal.first") : t("common.ordinal.other")}</sup>`;

function bar3(h: number, d: number, a: number): string {
  return `<div class="mp-bar"><span class="h" style="width:${pct(h)}"></span><span class="d" style="width:${pct(d)}"></span><span class="a" style="width:${pct(a)}"></span></div>`;
}
function bar2(homeShare: number): string {
  const h = Math.max(2, Math.min(98, Math.round(homeShare * 100)));
  return `<div class="mp-bar duo"><span class="h" style="width:${h}%"></span><span class="a" style="width:${100 - h}%"></span></div>`;
}
function dots(f: Form): string {
  return f.length ? f.map((r) => `<span class="fd ${r}"></span>`).join("") : `<span class="dim">—</span>`;
}

/** Bloc « Face-à-face » (avant-match) : bilan V/N/D vu du domicile + TOUTES les
 *  confrontations (scrollable). TOUJOURS rendu — état « aucune » si l'API n'a pas
 *  d'historique pour la paire — pour que la section ne disparaisse jamais en silence. */
function h2hBoxHtml(meetings: readonly H2HMeeting[], home: string, away: string): string {
  const n = meetings.length;
  const r = h2hRecord(meetings, home);
  const rows = meetings
    .map((mt) => {
      const d = mt.date ? new Date(mt.date) : null;
      const dt = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }) : "";
      return `<div class="mp-h2h-row"><span class="f fi fi-${esc(flagCode(mt.homeCode))}"></span><b>${mt.homeGoals}–${mt.awayGoals}</b><span class="f fi fi-${esc(flagCode(mt.awayCode))}"></span><span class="mp-h2h-d">${esc(dt)}</span></div>`;
    })
    .join("");
  const wins = (code: string, w: number): string =>
    `<span class="mp-h2h-side" title="${esc(teamName(code))} : ${t("fiche.h2h.winsTitle", { w })}">${flagSpan(code)}<b>${w}</b><span class="mp-h2h-vl">${t("common.winAbbr")}</span></span>`;
  const body = n
    ? `<div class="mp-h2h-sum">${wins(home, r.w)}<span class="mp-h2h-mid">${t("fiche.h2h.draws", { d: r.d })}</span>${wins(away, r.l)}</div>
    <details class="mp-h2h-det"><summary class="mp-h2h-tog"><i class="ti ti-chevron-right mp-h2h-chev" aria-hidden="true"></i>${t("fiche.h2h.meetings", { n })}</summary><div class="mp-h2h-list">${rows}</div></details>`
    : `<div class="mp-h2h-empty">${t("fiche.h2h.empty")}</div>`;
  return `<div class="mp-sec"><div class="mp-h"><span class="mid">${t("fiche.h2h.title")}</span></div>${body}</div>`;
}

function panelHtml(state: OfficialState, id: number): string | null {
  const view = state.byId.get(id);
  if (!view) return null;
  const m = view.match;
  // KO : home/away du calendrier sont des jetons (« 1F », « 3:CDFGH ») → on prend les
  // équipes RÉSOLUES du bracket (sinon Elo introuvable → la fiche ne s'ouvrait pas).
  const koNode = m.stage !== "group" ? state.bracket["M" + String(id)] : null;
  const home = koNode?.a ?? m.home;
  const away = koNode?.b ?? m.away;
  const eloH = state.currentElos[home] ?? initialElos[home];
  const eloA = state.currentElos[away] ?? initialElos[away];
  if (eloH === undefined || eloA === undefined) return null; // slot KO non résolu

  const effH = eloH + (m.hostCountry != null && hosts[home] === m.hostCountry ? modelHomeAdvantage : 0);
  const effA = eloA + (m.hostCountry != null && hosts[away] === m.hostCountry ? modelHomeAdvantage : 0);
  const p = outcomeProbabilities(effH, effA, poissonParams);
  const [xgH, xgA] = lambdas(effH, effA, poissonParams);
  const ft = view.displayScore ?? view.live?.score?.ft ?? null;
  const live = view.status === "live" || view.status === "paused";
  const played = live || view.status === "finished";

  // Statistiques RÉELLES (API-Football) si le match est JOUÉ : on les montre
  // exactement comme la carte « En direct » (mêmes classes .lf-st-*, mêmes
  // libellés, survol → graphe d'évolution). Sinon (match non joué), on retombe
  // sur la prédiction du modèle plus bas (probabilités / Elo / buts attendus).
  const s = view.live?.stats;
  const realStats = s
    ? `<div class="mp-sec"><div class="mp-h"><span class="mid">${live ? t("fiche.statsLive") : t("fiche.statsMatch")}</span></div></div>` +
      `<div class="lf-stats">` +
      ((meta) =>
        STAT_ORDER.map((k) =>
          duoStat(meta[k]?.label ?? k, s[k], meta[k]?.asPct ?? false, "lf-st", k),
        ).join(""))(statMeta()) +
      `</div>`
    : "";

  const head = m.stage === "group" ? t("lf.groupMatchdayAbbr", { group: m.group ?? "", matchday: m.matchday ?? "" }) : roundName(m.stage);
  const scoreMid = ft
    ? `<div class="mp-score ${live ? "live" : ""}">${ft[0]} – ${ft[1]}</div>`
    : `<div class="mp-score time">${esc(fmtTime(m.utcDateTime))}</div>`;

  const stH = standingOf(state, home);
  const stA = standingOf(state, away);
  const standing =
    stH && stA
      ? `<div class="mp-sec"><div class="mp-h"><span>${ord(stH.rank)} · ${stH.points} pts</span><span class="mid">${t("fiche.standing")}</span><span>${ord(stA.rank)} · ${stA.points} pts</span></div></div>`
      : "";

  // Faits du match (timeline COMPLÈTE : buts + cartons + remplacements, noms
  // cliquables, défilante) + compositions : EXACTEMENT les blocs de la carte
  // « En direct », réutilisés → la fiche d'un match passé = le live.
  const events = played ? eventsHtml(state, view) : "";
  const eventsSec = events
    ? `<div class="mp-sec"><div class="mp-h"><span class="mid">${live ? t("fiche.eventsLive") : t("fiche.eventsMatch")}</span></div></div>${events}`
    : "";
  const lineups = played ? lineupsSection(view) : "";

  // Métriques dérivées : indice de DOMINATION (combine xG/possession/tirs/corners)
  // + RÉSULTAT MÉRITÉ (buts vs xG) — seulement quand on a des stats réelles.
  const dom = computeDominance(s);
  const domHtml = dom
    ? `<div class="mp-sec"><div class="mp-h"><span>${dom.home}</span><span class="mid">${t("fiche.dominance")}</span><span>${dom.away}</span></div>${bar2(dom.home / 100)}</div>`
    : "";
  const xg = s?.xg;
  const deservedHtml =
    s && ft && xg && xg[0] != null && xg[1] != null
      ? (() => {
          const cell = (xv: number, gv: number): string => {
            const d = deservedResult(xv, gv);
            return `<div class="mp-dz"><span class="mp-dz-x">${t("fiche.deserved.cell", { xg: xv.toFixed(1), goals: gv })}</span>${d ? `<span class="mp-dz-t ${d.tone}">${esc(d.label)}</span>` : ""}</div>`;
          };
          return `<div class="mp-sec"><div class="mp-h"><span class="mid">${t("fiche.deservedTitle")}</span></div><div class="mp-deserved">${cell(xg[0], ft[0])}${cell(xg[1], ft[1])}</div></div>`;
        })()
      : "";

  return `
    <div class="mp-head">
      <div class="mp-team">${flagSpan(home, { name: true })}${teamRatingTag(m.id, home)}</div>
      ${scoreMid}
      <div class="mp-team a">${flagSpan(away, { name: true })}${teamRatingTag(m.id, away)}</div>
    </div>
    <div class="mp-meta">${esc(head)}${m.city ? ` · ${esc(m.city)}` : ""}${
      view.live?.score?.ht ? ` · ${t("fiche.htScore", { h: view.live.score.ht[0], a: view.live.score.ht[1] })}` : ""
    }</div>
    ${eventsSec}
    ${realStats}
    ${domHtml}
    ${deservedHtml}
    ${lineups}
    ${
      s
        ? "" // match joué : les stats réelles ci-dessus remplacent la prédiction
        : `<div class="mp-sec mp-phase"><div class="mp-h"><span class="mid">${t("fiche.preMatch")}</span></div></div>
    <div class="mp-h2h" data-h2h></div>
    <div class="mp-sec">
      <div class="mp-h"><span>${fmtPct(p.homeWin, 0)}</span><span class="mid">${t("fiche.probabilityDraw", { draw: fmtPct(p.draw, 0) })}</span><span>${fmtPct(p.awayWin, 0)}</span></div>
      ${bar3(p.homeWin, p.draw, p.awayWin)}
    </div>
    <div class="mp-sec">
      <div class="mp-h"><span>${Math.round(eloH)}</span><span class="mid">${t("lf.strengthElo")}</span><span>${Math.round(eloA)}</span></div>
      ${bar2(winProbability(effH, effA))}
    </div>
    <div class="mp-sec">
      <div class="mp-h"><span>${xgH.toFixed(1)}</span><span class="mid">${t("fiche.expectedGoalsModel")}</span><span>${xgA.toFixed(1)}</span></div>
      ${bar2(xgH / (xgH + xgA))}
    </div>
    <div class="mp-sec">
      <div class="mp-h"><span class="mid">${t("fiche.recentForm")}</span></div>
      <div class="mp-forms"><div class="fside">${dots(teamForm(state, home))}</div><div class="fside a">${dots(teamForm(state, away))}</div></div>
    </div>`
    }
    ${s ? `<div class="mp-h2h" data-h2h></div>` : ""}
    ${standing}`;
}

// --- contrôleur (survol des cartes de match) -------------------------------

interface Provider {
  root: HTMLElement;
  get: () => OfficialState | null;
}
const providers: Provider[] = [];
let panel: HTMLElement | null = null;
let overlay: HTMLElement | null = null;
let overlayOpen = false;
let installed = false;

function ensurePanel(): HTMLElement {
  if (!panel) {
    panel = document.createElement("div");
    panel.className = "mp hide";
    document.body.appendChild(panel);
  }
  return panel;
}

// --- overlay détail (clic) -------------------------------------------------
// Le survol donne un aperçu rapide (desktop) ; le CLIC ouvre une grande carte
// centrée qui « monte » — mêmes stats complètes que la carte live, pour les
// matchs passés aussi, et accessible au doigt (pas de survol sur mobile).
function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  const ov = document.createElement("div");
  ov.className = "md-overlay hide";
  ov.innerHTML = `<div class="md-card" role="dialog" aria-modal="true" aria-label="${t("fiche.overlayAria")}"><button type="button" class="md-close" aria-label="${t("fiche.closeDetail")}"><i class="ti ti-x" aria-hidden="true"></i></button><div class="md-body"></div></div>`;
  ov.addEventListener("click", (e) => {
    // fond (hors carte) OU bouton fermer (délégation robuste : capte le clic même
    // si un navigateur rate le hit-test direct sur le bouton au-dessus du blur/transform).
    if (e.target === ov || (e.target instanceof Element && e.target.closest(".md-close"))) closeOverlay();
  });
  ov.querySelector(".md-close")?.addEventListener("click", () => closeOverlay());
  overlay = ov;
  document.body.appendChild(ov);
  return ov;
}
function openOverlay(id: number, state: OfficialState | null): void {
  if (!state) return;
  const html = panelHtml(state, id);
  if (!html) return;
  track("fiche", { kind: "match", id });
  panel?.classList.add("hide"); // masque l'aperçu au survol
  const ov = ensureOverlay();
  const card = ov.querySelector<HTMLElement>(".md-card");
  const body = ov.querySelector<HTMLElement>(".md-body");
  if (!card || !body) return;
  card.dataset.match = String(id); // permet au graphe d'évolution de retrouver le match
  body.innerHTML = html;
  animateStatBars(card, id); // révèle les barres de stats en cascade (comme le live)
  // Face-à-face (avant-match) : 1 appel serveur PARESSEUX (mis en cache), rempli en
  // différé pour ne pas bloquer l'ouverture. Uniquement à l'overlay (pas au survol).
  const mv = state.byId.get(id);
  if (mv) {
    const { home, away } = mv.match;
    void fetchH2H(home, away).then((meetings) => {
      if (!overlayOpen || card.dataset.match !== String(id) || !meetings) return;
      const slot = body.querySelector<HTMLElement>("[data-h2h]");
      if (slot) slot.outerHTML = h2hBoxHtml(meetings, home, away);
    });
  }
  ov.classList.remove("hide");
  ov.style.zIndex = String(frontZ()); // au-dessus de toute modale déjà ouverte
  overlayOpen = true;
  requestAnimationFrame(() => ov.classList.add("open")); // déclenche la montée
}
function closeOverlay(): void {
  if (!overlay || !overlayOpen) return;
  overlayOpen = false;
  overlay.classList.remove("open");
  const ov = overlay;
  setTimeout(() => {
    if (!overlayOpen) ov.classList.add("hide"); // masque après la transition (sauf ré-ouverture)
  }, 300);
}
function stateFor(card: HTMLElement): OfficialState | null {
  for (const pr of providers) {
    if (pr.root.isConnected && pr.root.contains(card)) {
      const s = pr.get();
      if (s) return s;
    }
  }
  // Carte hors d'une vue enregistrée — typiquement dans un overlay monté sur
  // <body> (fiche pays) : on retombe sur le 1er état dispo (le dashboard).
  for (const pr of providers) {
    const s = pr.get();
    if (s) return s;
  }
  return null;
}
function place(card: HTMLElement, el: HTMLElement): void {
  const r = card.getBoundingClientRect();
  el.style.left = "0px";
  el.style.top = "0px";
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
  let top = r.bottom + 8;
  if (top + h > window.innerHeight - 8) top = Math.max(8, r.top - h - 8);
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}
function showFor(card: HTMLElement): void {
  const id = Number(card.dataset.match);
  if (!id) return;
  const state = stateFor(card);
  if (!state) return;
  const html = panelHtml(state, id);
  if (!html) return;
  const el = ensurePanel();
  el.dataset.match = String(id); // permet au graphe d'évolution de retrouver le match
  el.innerHTML = html;
  el.classList.remove("hide");
  place(card, el);
}
function cardFrom(e: Event): HTMLElement | null {
  const t = e.target;
  if (!(t instanceof Element)) return null;
  return t.closest<HTMLElement>(".today-card[data-match]");
}
/** Vrai si l'évènement vise un drapeau (priorité au tooltip de parcours). */
function onFlag(e: Event): boolean {
  return e.target instanceof Element && e.target.closest(".f[data-team]") !== null;
}

/** Enregistre une vue (conteneur + accès à son état) et installe l'écouteur. */
export function registerMatchPanel(root: HTMLElement, get: () => OfficialState | null): void {
  providers.push({ root, get });
  if (installed) return;
  installed = true;
  document.addEventListener("mouseover", (e) => {
    if (overlayOpen) return; // l'overlay détail couvre l'aperçu
    if (onFlag(e)) {
      // survol d'un drapeau dans la carte : on laisse le tooltip de parcours
      // d'équipe prendre la main et on masque le panneau de stats du match.
      panel?.classList.add("hide");
      return;
    }
    const c = cardFrom(e);
    if (c) showFor(c);
  });
  document.addEventListener("mouseout", (e) => {
    if (overlayOpen) return;
    const c = cardFrom(e);
    if (!c) return;
    const related = e instanceof MouseEvent ? e.relatedTarget : null;
    if (related instanceof Node && c.contains(related)) return; // toujours dans la carte
    panel?.classList.add("hide");
  });
  // CLIC/TAP sur N'IMPORTE quel match [data-match] (bande du jour, carte live,
  // cellule du bracket…) → grande carte détail centrée (la « fiche match »).
  // Priorité : un joueur (fiche joueur), un contrôle de focus (data-pin) ou un
  // drapeau (parcours d'équipe) l'emportent sur l'ouverture de la fiche match.
  document.addEventListener("click", (e) => {
    if (onFlag(e)) return;
    const t = e.target;
    if (t instanceof Element && t.closest("[data-player],[data-pin]")) return;
    const card = t instanceof Element ? t.closest<HTMLElement>("[data-match]") : null;
    // La GRANDE carte focus est déjà la vue détaillée → on ne la rouvre pas en
    // overlay (et on évite les ouvertures accidentelles en cliquant dedans).
    // `.md-card` = la carte de L'OVERLAY lui-même (elle porte data-match pour le
    // graphe d'évolution) : sans cette garde, cliquer le bouton fermer rouvrait
    // aussitôt la fiche (bug propre aux fiches MATCH ; pays/joueur n'ont pas data-match).
    if (!card || card.classList.contains("live-focus") || card.classList.contains("md-card")) return;
    const id = Number(card.dataset.match);
    if (id) openOverlay(id, stateFor(card));
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeOverlay();
  });
}
